// Mirror of backend `models/src/bi/dashboard.ts` (the wire types only) and
// of the generated enums `BiDashboardVisibility`,
// `BiDashboardGrantPermission` and `BiRevisionSource`.

import { BiCardQuery } from './blend';
import { BiDraftResult } from './draft';
import { BiTimeRange } from './query';
import { BiPalette, BiVizSpec } from './viz';

/** Columns of the desktop grid a custom layout is drawn on. */
export const kBiGridColumns = 12;
/** Tallest a custom layout item may be, in grid rows. */
export const kBiMaxItemHeight = 24;
export const kBiMaxDashboardName = 100;
export const kBiMaxGrantsPerDashboard = 200;

export enum BiDashboardVisibility {
  Private = 1,
  Store = 2,
}

export enum BiDashboardGrantPermission {
  View = 1,
  Edit = 2,
}

export enum BiRevisionSource {
  Manual = 1,
  Ai = 2,
  Undo = 3,
  Add = 4,
  Create = 5,
}

export enum BiLayoutMode {
  /** Cards flow into columns in `order`; heights are client-owned. */
  Preset = 'preset',
  /** The user dragged or resized: positions on the 12-column grid. */
  Custom = 'custom',
}

/** `auto` picks 2 or 3 from the viewport width. */
export type BiPresetColumns = 'auto' | 2 | 3;
/** A preset card spans one column, or the full width. */
export type BiPresetSpan = 1 | 'full';

/**
 * Grid rows a card takes in the one-column phone view where a viewer
 * resized it there; never read by the wide grid. A `set_layout` without it
 * keeps the stored one.
 */
export type BiNarrowRows = Record<string, number>;

export interface BiPresetLayout {
  mode: BiLayoutMode.Preset;
  columns: BiPresetColumns;
  /** Card ids, top to bottom. */
  order: string[];
  /** Cards not listed span one column. */
  spans?: Record<string, BiPresetSpan>;
  narrowRows?: BiNarrowRows;
}

export interface BiLayoutItem {
  cardId: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface BiCustomLayout {
  mode: BiLayoutMode.Custom;
  items: BiLayoutItem[];
  narrowRows?: BiNarrowRows;
}

export type BiDashboardLayout = BiPresetLayout | BiCustomLayout;

export enum BiPlacementEdge {
  Top = 'top',
  Bottom = 'bottom',
}

export type BiPlacementAnchor =
  | BiPlacementEdge
  | { before: string }
  | { after: string };

export enum BiPlacementSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
  Full = 'full',
}

export interface BiPlacement {
  anchor: BiPlacementAnchor;
  size?: BiPlacementSize;
}

/** v1: one date range over each card's primary time field. */
export interface BiDashboardFilters {
  dateRange?: BiTimeRange;
}

export enum BiDashboardOpKind {
  AddCard = 'add_card',
  UpdateCard = 'update_card',
  RemoveCard = 'remove_card',
  PlaceCard = 'place_card',
  SetLayout = 'set_layout',
  RenameDashboard = 'rename_dashboard',
  SetFilters = 'set_filters',
  SetPalette = 'set_palette',
}

export interface BiAddCardOp {
  op: BiDashboardOpKind.AddCard;
  draftId: string;
  placement?: BiPlacement;
}

export interface BiUpdateCardOp {
  op: BiDashboardOpKind.UpdateCard;
  cardId: string;
  title?: string;
  querySpec?: BiCardQuery;
  vizSpec?: BiVizSpec;
}

export interface BiRemoveCardOp {
  op: BiDashboardOpKind.RemoveCard;
  cardId: string;
}

export interface BiPlaceCardOp {
  op: BiDashboardOpKind.PlaceCard;
  cardId: string;
  placement: BiPlacement;
}

export interface BiSetLayoutOp {
  op: BiDashboardOpKind.SetLayout;
  layout: BiDashboardLayout;
}

export interface BiRenameDashboardOp {
  op: BiDashboardOpKind.RenameDashboard;
  name: string;
  /** Omitted: unchanged; null: cleared. */
  description?: string | null;
}

export interface BiSetFiltersOp {
  op: BiDashboardOpKind.SetFilters;
  filters: BiDashboardFilters;
}

/**
 * The colors every card uses unless it sets its own `palette` or
 * `seriesColors`.
 */
export interface BiSetPaletteOp {
  op: BiDashboardOpKind.SetPalette;
  palette: BiPalette;
}

export type BiDashboardOp =
  | BiAddCardOp
  | BiUpdateCardOp
  | BiRemoveCardOp
  | BiPlaceCardOp
  | BiSetLayoutOp
  | BiRenameDashboardOp
  | BiSetFiltersOp
  | BiSetPaletteOp;

/** What the caller may do with a dashboard; each level includes the last. */
export enum BiDashboardPermission {
  None = 'none',
  View = 'view',
  Edit = 'edit',
  Manage = 'manage',
}

/**
 * A staff dashboard's subject as the picker labels them: one who both
 * advises and does technical work reads as an advisor.
 */
export enum BiSubjectRole {
  Advisor = 'advisor',
  Technician = 'technician',
}

interface BiDashboardHeader {
  id: string;
  name: string;
  description: string | null;
  visibility: BiDashboardVisibility;
  ownerMemberId: number;
  ownerName: string | null;
  /**
   * The member a template dashboard is about (a staff dashboard, or one's
   * own 我的看板); null on every other dashboard.
   */
  subjectMemberId: number | null;
  subjectName: string | null;
  /** What the subject does; null without a subject. */
  subjectRole: BiSubjectRole | null;
  /** The caller's default dashboard in this store. */
  isDefault: boolean;
  revision: number;
  /** The caller's permission. */
  permission: BiDashboardPermission;
  updatedAt: string;
}

export interface BiDashboardSummary extends BiDashboardHeader {
  cardCount: number;
}

/**
 * Where BI opens for the caller: their default dashboard, or null when
 * they can see no dashboard in the store.
 */
export interface BiDashboardEntry {
  dashboardId: string | null;
}

/** A member's default set, created in one go; their default comes first. */
export interface BiCreateDefaultDashboardsResult {
  dashboards: BiDashboardSummary[];
}

export interface BiCardView {
  id: string;
  title: string;
  spec: BiCardQuery;
  viz: BiVizSpec;
  sourceDraftId: string | null;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
}

export interface BiDashboardView extends BiDashboardHeader {
  /** Repaired on read: every live card placed exactly once. */
  layout: BiDashboardLayout;
  filters: BiDashboardFilters;
  /** What cards without their own palette or series colors use. */
  palette: BiPalette;
  cards: BiCardView[];
  createdAt: string;
}

export interface BiDashboardGrantInput {
  memberId: number;
  permission: BiDashboardGrantPermission;
}

export interface BiDashboardGrantView extends BiDashboardGrantInput {
  memberName: string | null;
  createdBy: number;
  createdAt: string;
}

export interface BiDashboardRevisionView {
  revision: number;
  source: BiRevisionSource;
  changeSetId: string | null;
  createdBy: number;
  createdByName: string | null;
  createdAt: string;
  cardCount: number;
}

export interface BiCreateDashboardRequest {
  name: string;
  description?: string | null;
  /** Default: private. */
  visibility?: BiDashboardVisibility;
}

export interface BiApplyOpsRequest {
  baseRevision: number;
  ops: BiDashboardOp[];
}

export interface BiAddedCard {
  draftId: string;
  cardId: string;
}

export interface BiApplyOpsResult {
  dashboard: BiDashboardView;
  /** One entry per `add_card`, including a draft that was already on it. */
  addedCards: BiAddedCard[];
}

export interface BiUndoRequest {
  /** The revision to undo; it must be the current one. */
  revision: number;
}

export interface BiReplaceGrantsRequest {
  grants: BiDashboardGrantInput[];
}

/** Filters only: a card always runs its saved spec. */
export interface BiCardDataRequest {
  /** Overrides the dashboard's saved filters for this load. */
  filters?: BiDashboardFilters;
}

export enum BiCardDataState {
  Ready = 'ready',
  /** The viewer's role cannot use a field the card reads: title only. */
  Restricted = 'restricted',
}

export enum BiDateFilterUse {
  /** No dashboard date range: the card's own time range applies. */
  None = 'none',
  Applied = 'applied',
  /** The card's dataset has no time field. */
  Ignored = 'ignored',
}

export type BiCardData =
  | {
      state: BiCardDataState.Ready;
      cardId: string;
      result: BiDraftResult;
      dateFilter: BiDateFilterUse;
    }
  | { state: BiCardDataState.Restricted; cardId: string };
